import {
  faCancel,
  faDownload,
  faEdit,
  faEye,
  faFileInvoice,
  faPlus,
  faSearch,
  faSpinner,
  faTrash,
  faTrashAlt,
  faTurnDown,
  faUpDown,
  faUpload,
  faUpLong,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import 'chart.js/auto'
import { subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Doughnut } from 'react-chartjs-2'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Link, useNavigate, useNavigation } from 'react-router-dom'
import { toast } from 'react-toastify'
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode.js'

const ListaNFe = () => {
  // Estados principais
  const [nfes, setNfes] = useState([])
  const [empresa, setEmpresas] = useState([])
  const [empresaSelecionada, setEmpresaSelecionada] = useState('')
  const [termoPesquisa, setTermoPesquisa] = useState('')
  const [tipoPesquisa, setTipoPesquisa] = useState('numeroNfe')
  const [isLoading, setLoading] = useState(false)
  const [dataInicial, setDataInicial] = useState(null)
  const [dataFinal, setDataFinal] = useState(null)

  // Estados de loading para diferentes ações
  const [loadingStates, setLoadingStates] = useState({
    protocolo: {},
    consulta: {},
    cancelamento: {},
    dadosFiscais: {},
    delete: {},
  })

  // Estado de estatísticas
  const [stats, setStats] = useState({
    total: 0,
    transmitidas: 0,
    erros: 0,
    canceladas: 0,
    emDigitacao: 0,
  })

  const navigate = useNavigate()

  // Função auxiliar para atualizar estados de loading
  const setLoadingState = useCallback((type, id, value) => {
    setLoadingStates((prev) => ({
      ...prev,
      [type]: { ...prev[type], [id]: value },
    }))
  }, [])

  // Atualizar prefixo de link
  const updateLinkPrefix = (link) => {
    return (
      link?.replace(
        'http://erp.belahome.com.br:9091',
        'http://fiscal.ziphub.com.br',
      ) || ''
    )
  }

  // Componente de gráfico circular melhorado
  const CircularProgressBar = ({ percentage, label }) => {
    const data = {
      datasets: [
        {
          data: [percentage, 100 - percentage],
          backgroundColor: [
            'rgba(220, 53, 69, 0.8)',
            'rgba(214, 214, 214, 0.2)',
          ],
          borderWidth: 0,
        },
      ],
    }

    const options = {
      cutout: '70%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      maintainAspectRatio: true,
    }

    return (
      <div className="text-center">
        <div
          style={{
            width: '80px',
            height: '80px',
            margin: '0 auto',
            position: 'relative',
          }}
        >
          <Doughnut
            data={data}
            options={options}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#dc3545',
            }}
          >
            {percentage}%
          </div>
        </div>
        <small className="text-muted mt-2 d-block">{label}</small>
      </div>
    )
  }

  // Calcular estatísticas das NF-es
  const calcularEstatisticas = useCallback((nfes) => {
    const stats = {
      total: nfes.length,
      transmitidas: nfes.filter((n) => n.status === 'T').length,
      erros: nfes.filter((n) => n.status === 'R').length,
      canceladas: nfes.filter((n) => n.status === 'I').length,
      emDigitacao: nfes.filter((n) => n.status === 'D').length,
    }
    setStats(stats)
  }, [])

  // Handlers de ações
  const handleXml = useCallback(async (idnfe, chave) => {
    try {
      const response = await apinode({
        url: `/nfe/fiscal_xml/${localStorage.idconta}`,
        method: 'GET',
        params: { id: idnfe },
        responseType: 'blob',
      })

      const text = await response.data.text()
      const url = window.URL.createObjectURL(new Blob([text]))
      const linkElement = document.createElement('a')
      linkElement.href = url
      linkElement.setAttribute('download', `${chave}-nfe.xml`)
      document.body.appendChild(linkElement)
      linkElement.click()
      document.body.removeChild(linkElement)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Erro ao baixar XML:', error)
      toast.error('Erro ao baixar o arquivo XML')
    }
  }, [])

  const handlePdf = useCallback(async (link, idnfe) => {
    try {
      if (!link || !link.includes('http://erp.belahome.com.br:9091')) {
        const response = await apinode({
          url: `/nfe/fiscal_pdf/${localStorage.idconta}`,
          method: 'GET',
          params: { id: idnfe },
        })

        const newWindow = window.open('', '_blank')
        newWindow.document.body.innerHTML = response.data
        newWindow.print()
      } else {
        const updatedLink = updateLinkPrefix(link)
        window.open(updatedLink, '_blank')
      }
    } catch (error) {
      console.error('Erro ao abrir PDF:', error)
      toast.error('Erro ao visualizar o PDF')
    }
  }, [])

  const handleProtocolo = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('protocolo', id, true)
        const response = await apinode.get(
          `/nfe/protocolo/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast.success(response.data)
      } catch (error) {
        toast.error('Erro ao consultar protocolo')
        console.error(error)
      } finally {
        setLoadingState('protocolo', id, false)
      }
    },
    [setLoadingState],
  )

  const handleConsultarStatus = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('consulta', id, true)
        const response = await apinode.get(
          `/nfe/consultar/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast[response.data.status ? 'success' : 'warning'](
          response.data.message,
        )
      } catch (error) {
        toast.error('Erro ao consultar status')
        console.error(error)
      } finally {
        setLoadingState('consulta', id, false)
      }
    },
    [setLoadingState],
  )

  const handleCancelarNFE = useCallback(
    async (id, contaEmp) => {
      try {
        const confirmed = window.confirm('Deseja realmente cancelar esta NF-e?')
        if (!confirmed) return

        setLoadingState('cancelamento', id, true)
        const response = await apinode.get(
          `/nfe/cancelamento_nfe/${id}/${localStorage.idconta}`,
        )
        toast.success(response.data.retorno)
        fetchNfesInicial(empresaSelecionada)
      } catch (error) {
        toast.error('Erro ao cancelar NF-e')
        console.error(error)
      } finally {
        setLoadingState('cancelamento', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleEditarCep = useCallback(
    async (id) => {
      try {
        const cepDestinatario = window.prompt('Digite o novo CEP:')
        if (!cepDestinatario) return

        const cepLimpo = cepDestinatario.replace(/\D/g, '')
        if (cepLimpo.length !== 8) {
          toast.error('CEP inválido. Deve conter 8 dígitos.')
          return
        }

        const confirmed = window.confirm(
          `Confirma atualizar o CEP para ${cepLimpo}?`,
        )
        if (!confirmed) return

        setLoadingState('cancelamento', id, true)
        const response = await apinode.post(
          `/ajuste-cep-edita/${id}/${localStorage.idconta}`,
          { cepDestinatario: cepLimpo },
        )

        toast.success('CEP atualizado com sucesso!')
        fetchNfesInicial(empresaSelecionada)
      } catch (error) {
        toast.error('Erro ao atualizar o CEP')
        console.error(error)
      } finally {
        setLoadingState('cancelamento', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleDelete = useCallback(
    async (id) => {
      try {
        const confirmed = window.confirm(
          'Tem certeza de que deseja excluir esta NF-e?',
        )
        if (!confirmed) return

        setLoadingState('delete', id, true)
        const response = await apinode.delete(
          `/nfe/${id}/${localStorage.idconta}`,
        )
        toast.success(response.data.message)
        fetchNfesInicial(empresaSelecionada)
      } catch (error) {
        toast.error('Erro ao excluir a NF-e')
        console.error(error)
      } finally {
        setLoadingState('delete', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleDadosFicais = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('dadosFiscais', id, true)
        await apinode.get(
          `/nfe/dadosfiscais/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast.success('Dados fiscais enviados com sucesso!')
      } catch (error) {
        toast.error('Erro ao enviar os dados fiscais!')
        console.error(error)
      } finally {
        setLoadingState('dadosFiscais', id, false)
      }
    },
    [setLoadingState],
  )

  // Fetch de dados
  const fetchEmpresa = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apinode.get(
        `/nfe/lista-empresa/${localStorage.idconta}`,
      )
      setEmpresas(response.data)

      if (response.data.length > 0) {
        setEmpresaSelecionada(response.data[0].id_conta)
        await fetchNfesInicial(response.data[0].id_conta)
      }
    } catch (error) {
      console.error('Erro ao obter empresas:', error)
      toast.error('Erro ao carregar lista de empresas')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchNfesInicial = useCallback(
    async (idConta) => {
      try {
        setLoading(true)
        const hoje = new Date()
        const dataInicialCalculada = subDays(hoje, 5)

        const response = await apinode.get(
          `/nfe/lista-nfe/${idConta}/${localStorage.idconta}`,
          {
            params: {
              dataIn: dataInicialCalculada,
              dataFn: hoje,
            },
          },
        )

        setNfes(response.data)
        calcularEstatisticas(response.data)
      } catch (error) {
        console.error('Erro ao obter NF-es:', error)
        toast.error('Erro ao carregar lista de NF-es')
      } finally {
        setLoading(false)
      }
    },
    [calcularEstatisticas],
  )

  const fetchNfes = useCallback(
    async (idConta) => {
      try {
        if (!dataInicial || !dataFinal) {
          toast.warning('Selecione as datas inicial e final')
          return
        }

        if (
          dataInicial.toISOString().split('T')[0] ===
          dataFinal.toISOString().split('T')[0]
        ) {
          toast.warning('Data inicial não pode ser igual à data final')
          return
        }

        setLoading(true)
        const response = await apinode.get(
          `/nfe/lista-nfe/${idConta}/${localStorage.idconta}`,
          {
            params: {
              dataIn: dataInicial,
              dataFn: dataFinal,
            },
          },
        )

        setNfes(response.data)
        calcularEstatisticas(response.data)
      } catch (error) {
        console.error('Erro ao obter NF-es:', error)
        toast.error('Erro ao pesquisar NF-es')
      } finally {
        setLoading(false)
      }
    },
    [dataInicial, dataFinal, calcularEstatisticas],
  )

  // Filtrar NF-es
  const nfesFiltradas = useMemo(() => {
    if (!termoPesquisa) return nfes

    return nfes.filter((nfe) => {
      const termo = termoPesquisa.toLowerCase()
      switch (tipoPesquisa) {
        case 'numeroNfe':
          return (nfe.numnfe || '').toString().includes(termoPesquisa)
        case 'destinatario':
          return nfe.pes_razao.toLowerCase().includes(termo)
        case 'id':
          return nfe.id.toString().includes(termoPesquisa)
        case 'numvenda':
          return (nfe.numero_venda || '').toString().includes(termoPesquisa)
        default:
          return true
      }
    })
  }, [nfes, termoPesquisa, tipoPesquisa])

  // Efeito inicial
  useEffect(() => {
    const hoje = new Date()
    const dataInicialCalculada = subDays(hoje, 5)
    setDataInicial(dataInicialCalculada)
    setDataFinal(hoje)
    fetchEmpresa()
  }, [fetchEmpresa])

  // Helper para status
  const getStatusBadge = (status) => {
    const statusMap = {
      T: { bg: 'success', text: 'Transmitida' },
      R: { bg: 'danger', text: 'Erro' },
      D: { bg: 'info', text: 'Em Digitação' },
      I: { bg: 'warning', text: 'Cancelada' },
    }
    const config = statusMap[status] || {
      bg: 'secondary',
      text: 'Desconhecido',
    }
    return <Badge bg={config.bg}>{config.text}</Badge>
  }

  // Formatação de nome de usuário
  const formatarNomeUsuario = (nome) => {
    if (!nome) return ''
    return nome
      .replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .join(' ')
  }

  return (
    <>
      <Navbar />

      <div className="container-fluid mt-page">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    className="me-2"
                  />
                  Lista de NF-e
                </h2>
                <p className="text-muted mb-0">
                  Gerencie e consulte suas notas fiscais eletrônicas
                </p>
              </div>
              <Button
                variant="success"
                as={Link}
                to={`/nfe/editar/novo/${empresaSelecionada}`}
                disabled={!empresaSelecionada}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  className="me-2"
                />
                Nova NF-e
              </Button>
            </div>
          </div>
        </div>

        {/* Cards de estatísticas */}
        <div className="row mb-4">
          <div className="col-md-2">
            <Card className="text-center">
              <Card.Body>
                <h3 className="mb-0">{stats.total}</h3>
                <small className="text-muted">Total</small>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-2">
            <Card className="text-center border-success">
              <Card.Body>
                <h3 className="mb-0 text-success">{stats.transmitidas}</h3>
                <small className="text-muted">Transmitidas</small>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-2">
            <Card className="text-center border-danger">
              <Card.Body>
                <h3 className="mb-0 text-danger">{stats.erros}</h3>
                <small className="text-muted">Com Erro</small>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-2">
            <Card className="text-center border-warning">
              <Card.Body>
                <h3 className="mb-0 text-warning">{stats.canceladas}</h3>
                <small className="text-muted">Canceladas</small>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-2">
            <Card className="text-center border-info">
              <Card.Body>
                <h3 className="mb-0 text-info">{stats.emDigitacao}</h3>
                <small className="text-muted">Em Digitação</small>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-2">
            <Card>
              <Card.Body className="p-2">
                <CircularProgressBar
                  percentage={
                    stats.total > 0
                      ? Math.round((stats.erros / stats.total) * 100)
                      : 0
                  }
                  label="Taxa de Erro"
                />
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Empresa</Form.Label>
                <Form.Select
                  value={empresaSelecionada}
                  onChange={(e) => {
                    setEmpresaSelecionada(e.target.value)
                    fetchNfesInicial(e.target.value)
                  }}
                >
                  {empresa.map((emp) => (
                    <option
                      key={emp.id_conta}
                      value={emp.id_conta}
                    >
                      {emp.emp_razao} - {emp.emp_cnpj}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="col-md-2">
                <Form.Label>Data Inicial</Form.Label>
                <DatePicker
                  selected={dataInicial}
                  onChange={setDataInicial}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  locale={ptBR}
                />
              </div>

              <div className="col-md-2">
                <Form.Label>Data Final</Form.Label>
                <DatePicker
                  selected={dataFinal}
                  onChange={setDataFinal}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  locale={ptBR}
                />
              </div>

              <div className="col-md-2 d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={() => fetchNfes(empresaSelecionada)}
                  disabled={isLoading}
                  className="w-100"
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="me-2"
                  />
                  Buscar
                </Button>
              </div>

              <div className="col-md-2">
                <Form.Label>Tipo de Pesquisa</Form.Label>
                <Form.Select
                  value={tipoPesquisa}
                  onChange={(e) => setTipoPesquisa(e.target.value)}
                >
                  <option value="numeroNfe">Número da NF-e</option>
                  <option value="numvenda">Num. Pedido</option>
                  <option value="destinatario">Destinatário</option>
                  <option value="id">ID</option>
                </Form.Select>
              </div>

              <div className="col-md-10">
                <Form.Label>Pesquisar</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Digite o termo de pesquisa..."
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                  />
                  {termoPesquisa && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setTermoPesquisa('')}
                    >
                      Limpar
                    </Button>
                  )}
                </InputGroup>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Tabela */}
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '300px' }}>Ações</th>
                    <th>ID</th>
                    <th>Número</th>
                    <th>Série</th>
                    <th>Num. Venda</th>
                    <th>Data Emissão</th>
                    <th>Valor Total</th>
                    <th>Destinatário</th>
                    <th>Status</th>
                    <th>Usuário</th>
                    <th>Data Saída</th>
                    <th>Arquivos</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="12"
                        className="text-center py-5"
                      >
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          size="2x"
                        />
                        <p className="mt-2 mb-0">Carregando...</p>
                      </td>
                    </tr>
                  ) : nfesFiltradas.length === 0 ? (
                    <tr>
                      <td
                        colSpan="12"
                        className="text-center py-5"
                      >
                        <p className="mb-0">Nenhuma NF-e encontrada</p>
                      </td>
                    </tr>
                  ) : (
                    nfesFiltradas.map((nfe) => (
                      <tr key={nfe.id}>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  navigate(`/nfe/editar/${nfe.id}`)
                                }}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Enviar dados fiscais</Tooltip>}
                            >
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() =>
                                  handleDadosFicais(nfe.id, nfe.emp_conta)
                                }
                                disabled={loadingStates.dadosFiscais[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingStates.dadosFiscais[nfe.id]
                                      ? faSpinner
                                      : faUpload
                                  }
                                  spin={loadingStates.dadosFiscais[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Protocolo</Tooltip>}
                            >
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  handleProtocolo(nfe.id, nfe.emp_conta)
                                }
                                disabled={loadingStates.protocolo[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingStates.protocolo[nfe.id]
                                      ? faSpinner
                                      : faTurnDown
                                  }
                                  spin={loadingStates.protocolo[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Consultar Status</Tooltip>}
                            >
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  handleConsultarStatus(nfe.id, nfe.emp_conta)
                                }
                                disabled={loadingStates.consulta[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingStates.consulta[nfe.id]
                                      ? faSpinner
                                      : faUpDown
                                  }
                                  spin={loadingStates.consulta[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Cancelar</Tooltip>}
                            >
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() =>
                                  handleCancelarNFE(nfe.id, nfe.emp_conta)
                                }
                                disabled={loadingStates.cancelamento[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingStates.cancelamento[nfe.id]
                                      ? faSpinner
                                      : faCancel
                                  }
                                  spin={loadingStates.cancelamento[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Editar CEP</Tooltip>}
                            >
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleEditarCep(nfe.id)}
                                disabled={loadingStates.cancelamento[nfe.id]}
                              >
                                <FontAwesomeIcon icon={faUpLong} />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Excluir</Tooltip>}
                            >
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(nfe.id)}
                                disabled={loadingStates.delete[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingStates.delete[nfe.id]
                                      ? faSpinner
                                      : faTrash
                                  }
                                  spin={loadingStates.delete[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                        <td>{nfe.id}</td>
                        <td>
                          <strong>{nfe.numnfe}</strong>
                        </td>
                        <td>{nfe.serie}</td>
                        <td>
                          <a
                            href={`/pedido/editar/${nfe.numero_venda}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {nfe.numero_venda}
                          </a>
                        </td>
                        <td>
                          {new Date(nfe.datavenda).toLocaleDateString('pt-BR')}
                        </td>
                        <td>
                          <strong>
                            {parseFloat(nfe.totalpedido).toLocaleString(
                              'pt-BR',
                              {
                                style: 'currency',
                                currency: 'BRL',
                              },
                            )}
                          </strong>
                        </td>
                        <td>
                          {nfe.pes_razao
                            .toLowerCase()
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </td>
                        <td>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>{nfe.erro || 'Sem erros'}</Tooltip>
                            }
                          >
                            <span>{getStatusBadge(nfe.status)}</span>
                          </OverlayTrigger>
                        </td>
                        <td>
                          <small>
                            {formatarNomeUsuario(nfe.usuario_geracao)}
                          </small>
                        </td>
                        <td>
                          <small>
                            {new Date(nfe.datasaida).toLocaleDateString(
                              'pt-BR',
                            )}
                            <br />
                            {new Date(nfe.datasaida).toLocaleTimeString(
                              'pt-BR',
                            )}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <OverlayTrigger
                              overlay={<Tooltip>Ver PDF</Tooltip>}
                            >
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handlePdf(nfe.link_nfe, nfe.id)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Baixar XML</Tooltip>}
                            >
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleXml(nfe.id, nfe.chave)}
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        {/* Footer com informações */}
        {nfesFiltradas.length > 0 && (
          <div className="mt-3 text-muted text-center">
            <small>
              Mostrando {nfesFiltradas.length} de {nfes.length} NF-es
            </small>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .table th {
          font-weight: 600;
          font-size: 0.875rem;
          white-space: nowrap;
        }

        .table td {
          vertical-align: middle;
          font-size: 0.875rem;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }

        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }

        .card {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          border-radius: 0.5rem;
        }

        .badge {
          font-weight: 500;
          padding: 0.35em 0.65em;
        }

        .form-label {
          font-weight: 500;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .mt-page {
          margin-top: 20px;
          padding: 0 15px;
        }

        @media (max-width: 768px) {
          .table {
            font-size: 0.75rem;
          }

          .btn-sm {
            padding: 0.2rem 0.4rem;
            font-size: 0.7rem;
          }
        }
      `}</style>
    </>
  )
}

export default ListaNFe
